import React, { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { resolveImage } from "@/lib/media";
import { ImageUpload } from "@/components/ImageUpload";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { rupees, toMinor } from "@/lib/format";

const TYPES = ["notice", "question", "classified"];
const blank = { post_type: "notice", title: "", body: "", price: "", contact_phone: "", image_url: "" };

export default function Community() {
  const { t, lang, tenant, auth } = useApp();
  usePageTitle(t("community.title")); // RF-21: per-page tab title
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [mine, setMine] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState(blank);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  // U9: the board is only "empty" once the first fetch has answered.
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (!tenant) return;
    try {
      const { data } = await api.get("/community/posts", {
        params: { tenant_id: tenant.id, post_type: filter || undefined },
      });
      setPosts(rows(data));
      if (auth) {
        const res = await api.get("/community/posts/mine");
        setMine(rows(res.data));
      }
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, filter, auth]);

  const submit = async () => {
    setBusy(true);
    try {
      // `price_minor_units` (paise); the typed rupee `price` stays out of the spread (extras are a 422).
      const { price, ...rest } = form;
      await api.post(
        "/community/posts",
        {
          ...rest,
          price_minor_units: form.post_type === "classified" && price ? toMinor(price) : null,
        },
        { params: { tenant_id: tenant.id } },
      );
      toast.success(t("community.submitted"));
      setForm(blank);
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const typeLabels = t("community.typeLabels");

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid="community-page">
      <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl">{t("community.title")}</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        {t("community.subtitle", { city: localized(lang, tenant, "name") })}
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter("")}
          data-testid="community-filter-all"
          className={`rounded-full border px-4 py-1.5 text-sm ${
            !filter ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
          }`}
        >
          {t("community.filterAll")}
        </button>
        {TYPES.map((type) => (
          <button
            key={type}
            onClick={() => setFilter(type)}
            data-testid={`community-filter-${type}`}
            className={`rounded-full border px-4 py-1.5 text-sm ${
              filter === type ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
            }`}
          >
            {t(`community.${type}`)}
          </button>
        ))}
        <Button
          className="ml-auto rounded-full"
          data-testid="community-new-post-btn"
          onClick={() => (auth ? setShowForm((v) => !v) : navigate("/login"))}
        >
          <Plus size={15} className="mr-1" /> {auth ? t("community.createTitle") : t("community.signInToPost")}
        </Button>
      </div>

      {showForm && auth && (
        <div className="mt-6 space-y-4 rounded-2xl border border-border p-6" data-testid="community-form">
          <div className="flex flex-wrap gap-2">
            {TYPES.map((type) => (
              <button
                key={type}
                data-testid={`community-type-${type}`}
                onClick={() => setForm({ ...form, post_type: type })}
                className={`rounded-full border px-3 py-1 text-xs ${
                  form.post_type === type
                    ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                    : "border-border"
                }`}
              >
                {typeLabels[type]}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-title">{t("community.postTitle")}</Label>
            <Input
              id="post-title"
              data-testid="post-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-body">{t("community.postBody")}</Label>
            <Textarea
              id="post-body"
              rows={4}
              data-testid="post-body-input"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </div>
          {form.post_type === "classified" && (
            <div className="space-y-1.5">
              <Label htmlFor="post-price">{t("community.price")}</Label>
              <Input
                id="post-price"
                data-testid="post-price-input"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="post-contact">{t("community.contact")}</Label>
            <Input
              id="post-contact"
              data-testid="post-contact-input"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            />
          </div>
          <ImageUpload
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            testId="post-image-upload"
          />
          <Button className="rounded-full" data-testid="post-submit-btn" disabled={busy} onClick={submit}>
            {busy ? t("common.saving") : t("community.submit")}
          </Button>
        </div>
      )}

      <div className="mt-8 space-y-4" data-testid="community-post-list">
        {!loaded && (
          <div className="space-y-4" data-testid="community-loading">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        )}
        {loaded && posts.length === 0 && <p className="text-muted-foreground">{t("community.empty")}</p>}
        {posts.map((post) => (
          <article key={post.id} className="rounded-2xl border border-border p-5" data-testid={`post-${post.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Badge variant="outline" className="mb-2">
                  {typeLabels[post.post_type]}
                </Badge>
                <h2 className="font-heading text-xl font-semibold">{post.title}</h2>
                <p className="text-xs text-muted-foreground">{post.author_name}</p>
              </div>
              {post.price ? <p className="font-mono text-lg">{rupees({ minor_units: post.price?.minor_units })}</p> : null}
            </div>
            {post.image_url && (
              <img
                src={resolveImage(post.image_url)}
                alt=""
                width={288}
                height={192}
                loading="lazy" decoding="async"
                className="mt-3 h-48 w-full rounded-xl object-cover sm:w-72"
              />
            )}
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{post.body}</p>
            <div className="mt-4 flex items-center gap-5 text-sm text-muted-foreground">
              <span
                className="flex items-center gap-1"
                role="img"
                aria-label={t("community.likesCount", { count: post.like_count })}
              >
                <Heart size={14} aria-hidden="true" /> {t("community.likesCount", { count: post.like_count })}
              </span>
              <span
                className="flex items-center gap-1"
                role="img"
                aria-label={t("community.commentsCount", { count: post.comment_count })}
              >
                <MessageSquare size={14} aria-hidden="true" /> {t("community.commentsCount", { count: post.comment_count })}
              </span>
              <Link
                to={`/community/${post.id}`}
                data-testid={`post-open-${post.id}`}
                className="font-medium text-[hsl(var(--primary))]"
              >
                {t("community.openPost")}
              </Link>
            </div>
          </article>
        ))}
      </div>

      {auth && mine.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading text-lg font-semibold">{t("community.myPosts")}</h2>
          <div className="mt-4 space-y-3" data-testid="my-community-posts">
            {mine.map((post) => (
              <div
                key={post.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border p-4"
                data-testid={`my-post-${post.id}`}
              >
                <span className="font-medium">{post.title}</span>
                <Badge variant="outline">
                  {post.status === "pending" ? t("community.pendingBadge") : t(`common.${post.status}`)}
                </Badge>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
